// @flow
import idx from 'idx';
import * as React from 'react';
import { graphql } from 'react-apollo';
import FilesTable from 'files/components/FilesTable';
import LoadingIndicator from 'components/LoadingIndicator';
import Text from 'components/Text';
import TitleHeader from 'components/TitleHeader';
import ScrollableArea from 'components/ScrollableArea';
import CenteredContainer from 'components/CenteredContainer';
import View from 'components/View';
import ErrorReporter from 'app/components/ErrorReporter';
import TriggerComposerButton from 'chat/components/TriggerComposerButton';
import moment from 'moment';
import Group from 'entities/Group';
import GetFilesTabQuery from '../queries/FilesTabQuery.gql';
import { type FilesTab } from '../queries/__generated__/FilesTab';

type Props = {
  group: Group,
  children: Function,
  data: ReactApolloData<FilesTab>,
};

const filesEventFields = file => {
  return {
    content_type: file.fileNote,
    message_uuid: file.fileMessageUUID,
  };
};

const baseFileTransforms = file => {
  return {
    fileBody: file.body,
    fileCreatedAt: file.createdAt,
    fileGetMonth: moment(file.createdAt).format('MMMM'),
    fileKey: file.key,
    fileMessageUUID: file.messageUUID,
    fileSenderAvatarUrl: idx(file, _ => _.sender.avatarUrl),
    fileSenderInitials: idx(file, (_: any) => _.sender.initials), // flow complains because only one of the three possible sender types has initials
    fileSenderName: idx(file, _ => _.sender.name),
  };
};

// Method that turns filesContent and LinkContent into a generic FileView
const transformFilesDataToViewData = (files: *): Array<*> => {
  return files.map(file => {
    file = file || {}; // necessary for flow as even file may be nullable as determined by the autogen type...
    if (file.__typename === 'FileContent') {
      return {
        ...baseFileTransforms(file),
        fileName: file.name,
        fileNote: file.contentType,
        filePreviewUrl: file.url,
        fileUrl: file.url,
      };
    } else if (file.__typename === 'LinkContent') {
      return {
        ...baseFileTransforms(file),
        fileName: idx(file, _ => _.linkPreview.title),
        fileNote: idx(file, _ => _.linkPreview.description),
        filePreviewUrl: idx(file, _ => _.linkPreview.imageUrl),
        fileUrl: idx(file, _ => _.linkPreview.url),
      };
    }
    return {};
  });
};

class FilesContainer extends React.Component<Props, {}> {
  // TODO
  //   - set query in the state
  //   - filter files in render() matching the query in the state
  //     or perform a new fetch with the search query if graphql supports it.
  //
  setQuery = query => {
    console.log('file container new query = ', query);
  };

  render() {
    const {
      data: { loading, error, classAnnouncementsContent },
    } = this.props;

    if (error) {
      return (
        <CenteredContainer className="FilesTab-body">
          {typeof error === 'string' && (
            <ErrorReporter error={error}>
              <Text>Error loading data</Text>
            </ErrorReporter>
          )}
        </CenteredContainer>
      );
    }

    if (loading) {
      return (
        <CenteredContainer className="FilesTab-body">
          {loading && <LoadingIndicator height={40} size="small" />}
        </CenteredContainer>
      );
    }

    const filesToLoad =
      classAnnouncementsContent && classAnnouncementsContent.content
        ? transformFilesDataToViewData(classAnnouncementsContent.content || [])
        : {};

    if (!filesToLoad || !filesToLoad.length) {
      return (
        <CenteredContainer className="FilesTab-body">
          <TitleHeader
            title="Welcome to Files"
            subtext="All of the files that you share to your class will appear here"
            action={<TriggerComposerButton label="Send a File" />}
          />
        </CenteredContainer>
      );
    }

    const filesTable = <FilesTable files={filesToLoad} filesEventFields={filesEventFields} />;

    return (
      <ScrollableArea className="FilesTab">
        <View gutter="small" scroll className="FilesTab-body">
          {filesTable}
        </View>
      </ScrollableArea>
    );
  }
}

const withData = graphql(GetFilesTabQuery, {
  options: ({ group }) => ({
    variables: {
      // if root / all classes selected then use null to get all files from graphQL
      classUuids: group.isRoot() ? null : [group.getUuid()],
      after: null,
    },
  }),
});

export default withData(FilesContainer);
export { transformFilesDataToViewData };
